import Inputmask from "inputmask";
import * as SurveyCore from "survey-core";

const INPUT_MASK_WIDGET_NAME = "maskedit";

function syncTextItemProp(propJson: any) {
  const { name } = propJson;
  propJson.onGetValue = (obj: any) => (obj.editor ? obj.editor[name] : obj[name]);
  propJson.onSetValue = (obj: any, val: any) => {
    if (obj.editor) {
      obj.editor[name] = val;
      return;
    }

    obj[name] = val;
  };
}

function syncMultipleTextProp(propJson: any) {
  const { name } = propJson;
  propJson.visibleIf = (obj: any) => obj.cellType === "text";
  propJson.onGetValue = (obj: any) => obj.templateQuestion[name];
  propJson.onSetValue = (obj: any, val: any) => {
    obj.templateQuestion[name] = val;
  };
}

function registerInputMaskProperties() {
  if (SurveyCore.Serializer.findProperty("text", "inputMask")) {
    return;
  }

  const properties = [
    {
      name: "autoUnmask",
      type: "boolean",
      category: "general",
      showMode: "form",
      default: true,
    },
    {
      name: "clearIncomplete",
      type: "boolean",
      category: "general",
      showMode: "form",
      default: true,
    },
    {
      name: "showMaskOnHover",
      type: "boolean",
      category: "general",
      showMode: "form",
      default: true,
    },
    {
      name: "inputFormat",
      showMode: "form",
      category: "general",
    },
    {
      name: "inputMask",
      showMode: "form",
      category: "general",
      default: "none",
      choices: ["none", "datetime", "currency", "decimal", "email", "phone", "ip"],
    },
    {
      name: "numericDigits",
      category: "general",
      visible: false,
    },
    {
      name: "numericGroupSeparator",
      category: "general",
      default: ",",
      visible: false,
    },
    {
      name: "options",
      category: "general",
      visible: false,
    },
    {
      name: "unmaskAsNumber:boolean",
      category: "general",
      visible: false,
    },
    {
      name: "prefix",
      category: "general",
      visible: false,
    },
    {
      name: "suffix",
      category: "general",
      visible: false,
    },
  ];

  SurveyCore.Serializer.addProperties("text", properties);
  properties.forEach((prop) => {
    if (prop.visible !== false) {
      syncTextItemProp(prop);
    }
  });

  SurveyCore.Serializer.addProperties("multipletextitem", properties);
  properties.forEach((prop) => {
    if (prop.visible !== false) {
      syncMultipleTextProp(prop);
    }
  });
}

function buildInputMaskOptions(question: any) {
  const options =
    typeof question.options === "object" && question.options !== null
      ? { ...question.options }
      : {};

  options.autoUnmask = question.autoUnmask ?? true;
  options.clearIncomplete = question.clearIncomplete ?? true;
  options.showMaskOnHover = question.showMaskOnHover ?? true;
  options.unmaskAsNumber = question.unmaskAsNumber ?? false;

  if (question.inputMask !== "none") {
    options.inputFormat = question.inputFormat;
  }

  if (question.inputMask === "currency" || question.inputMask === "decimal") {
    options.groupSeparator = question.numericGroupSeparator || ",";
    options.radixPoint = undefined;
    options.autoGroup = true;
    options.placeholder = "0";
  }

  if (question.inputMask === "currency") {
    options.digits = question.numericDigits || 2;
    options.digitsOptional = false;
    options.prefix = question.prefix || "";
    options.suffix = question.suffix || "";
    options.placeholder = "0";
  }

  return options;
}

function resolveMask(question: any) {
  if (question.inputMask === "phone" && question.inputFormat) {
    return question.inputFormat;
  }

  return question.inputMask !== "none" ? question.inputMask : question.inputFormat;
}

function applyInputMask(question: any, input: HTMLInputElement) {
  const mask = resolveMask(question);
  if (!mask) {
    return;
  }

  const options = buildInputMaskOptions(question);
  Inputmask(mask, options).mask(input);

  input.onblur = () => {
    if (!input.inputmask) {
      return;
    }

    if (question.value === input.inputmask.getemptymask()) {
      question.value = "";
    }
  };

  const customWidgetData =
    question.getType() === "multipletextitem"
      ? question.editorValue?.customWidgetData
      : question.customWidgetData;

  input.oninput = () => {
    if (customWidgetData) {
      customWidgetData.isNeedRender = true;
    }
  };

  const pushValue = () => {
    if (!input.inputmask) {
      return;
    }

    if (input.inputmask.isComplete()) {
      question.value = options.autoUnmask ? input.inputmask.unmaskedvalue() : input.value;
      return;
    }

    question.value = null;
  };

  input.addEventListener("focusout", pushValue);
  input.onchange = pushValue;

  const updateValue = () => {
    input.value = question.value ?? "";
  };

  question.valueChangedCallback = updateValue;
  updateValue();
}

export default function registerInputMaskWidget() {
  registerInputMaskProperties();

  if (SurveyCore.CustomWidgetCollection.Instance.getCustomWidgetByName(INPUT_MASK_WIDGET_NAME)) {
    return;
  }

  SurveyCore.CustomWidgetCollection.Instance.addCustomWidget({
    name: INPUT_MASK_WIDGET_NAME,
    isDefaultRender: true,
    widgetIsLoaded: () => typeof Inputmask !== "undefined",
    isFit: (question: any) => {
      if (question.getType() === "multipletext") {
        return true;
      }

      return (
        question.getType() === "text" && (question.inputMask !== "none" || question.inputFormat)
      );
    },
    afterRender: (question: any, el: HTMLElement) => {
      if (question.getType() !== "multipletext") {
        const input = (el.querySelector("input") || el) as HTMLInputElement;
        applyInputMask(question, input);
        return;
      }

      question.items.forEach((item: any) => {
        if (item.inputMask === "none" && !item.inputFormat) {
          return;
        }

        const input = el.querySelector(`#${item.editor.inputId}`) as HTMLInputElement | null;
        if (input) {
          applyInputMask(item, input);
        }
      });
    },
    willUnmount: (_question: any, el: HTMLElement) => {
      const input = (el.querySelector("input") || el) as HTMLInputElement & {
        inputmask?: { remove: () => void };
      };

      input.inputmask?.remove();
    },
  });
}
